/**
 * 🛒 PRODUCTS SHOWCASE - Grille de Produits Spatiale avec SONS !
 * Catégories de produits avec effets holographiques et sons interactifs
 * 
 * 🔊 CHAQUE RAYON A SON PROPRE SON CARACTÉRISTIQUE !
 * - Vodka: TING! (tchin/trinquer)
 * - Red Bull: PSHIIIT (canette)
 * - Champagne: POP! (bouchon qui saute)
 * - Whisky: CLIC (verre cristal)
 * - Bière: PSHIT mousseux
 * - CBD: AAAHHHH (soupir de relaxation)
 * - Puffs: PROUT 😂 (pet sonore)
 * - Animaux: WOUF/MIAOU
 * - etc.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Wine, Candy, Cigarette, Package, Sparkles, ShoppingBag, IceCream, Heart, Dog, Droplets, Beer, Home } from 'lucide-react';
import productSounds from '../../hooks/useProductSounds';

// 🎵 Import direct des fonctions de sons
const {
  playRedBullSound,
  playVodkaSound,
  playWineSound,
  playChampagneSound,
  playWhiskySound,
  playBeerSound,
  playCBDSound,
  playPuffSound,
  playDogSound,
  playCatSound,
  playBiscuitSound,
  playChipsSound,
  playCandySound,
  playIceCreamSound,
  playCottonCandySound,
  playBeautySound,
  playHygieneSound,
  playHouseSound,
  playLemonadeSound
} = productSounds;

const PRODUCT_CATEGORIES = [
  { 
    icon: Wine, 
    name: 'Vins & Spiritueux', 
    items: ['Vodkas', 'Whisky', 'Champagne', 'Vins'],
    color: '#722F37',
    gradient: 'from-red-900/50 to-red-950/30',
    borderColor: 'border-red-700/30',
    // 🔊 Sons multiples : on joue un son aléatoire parmi les alcools
    sounds: [playVodkaSound, playWhiskySound, playChampagneSound, playWineSound]
  },
  { 
    icon: Sparkles, 
    name: 'Boissons Energy', 
    items: ['Red Bull', 'Monster', 'Soft drinks'],
    color: '#00BFFF',
    gradient: 'from-cyan-900/50 to-cyan-950/30',
    borderColor: 'border-cyan-500/30',
    // 🔊 PSHIIIT - La canette qui s'ouvre !
    sounds: [playRedBullSound]
  },
  { 
    icon: Beer, 
    name: 'Bières', 
    items: ['Blondes', 'Brunes', 'IPA', 'Craft'],
    color: '#D4A017',
    gradient: 'from-amber-800/50 to-amber-950/30',
    borderColor: 'border-amber-500/30',
    // 🔊 PSHIT mousseux - La bière !
    sounds: [playBeerSound]
  },
  { 
    icon: Candy, 
    name: 'Confiseries', 
    items: ['Bonbons', 'Chocolats', 'Barbe à papa'],
    color: '#FF69B4',
    gradient: 'from-pink-900/50 to-pink-950/30',
    borderColor: 'border-pink-500/30',
    // 🔊 Sons sucrés aléatoires
    sounds: [playCandySound, playCottonCandySound]
  },
  { 
    icon: IceCream, 
    name: 'Glaces & Surgelés', 
    items: ['Glaces', 'Surgelés', 'Desserts'],
    color: '#87CEEB',
    gradient: 'from-sky-900/50 to-sky-950/30',
    borderColor: 'border-sky-500/30',
    // 🔊 SLURP - Glace léchée !
    sounds: [playIceCreamSound]
  },
  { 
    icon: Package, 
    name: 'Épicerie', 
    items: ['Chips', 'Biscuits', 'Conserves'],
    color: '#F4A460',
    gradient: 'from-orange-900/50 to-orange-950/30',
    borderColor: 'border-orange-500/30',
    // 🔊 CRUNCH - Chips & biscuits !
    sounds: [playChipsSound, playBiscuitSound]
  },
  { 
    icon: Droplets, 
    name: 'CBD**', 
    items: ['Huiles', 'Fleurs', 'Infusions'],
    color: '#2D5A45',
    gradient: 'from-emerald-900/50 to-emerald-950/30',
    borderColor: 'border-emerald-500/30',
    // 🔊 AAAHHHH - Soupir de relaxation !
    sounds: [playCBDSound]
  },
  { 
    icon: Cigarette, 
    name: 'Puffs*', 
    items: ['Rechargeables'],
    color: '#9966FF',
    gradient: 'from-violet-900/50 to-violet-950/30',
    borderColor: 'border-violet-500/30',
    // 🔊 PROUT 😂 - Le fameux !
    sounds: [playPuffSound]
  },
  { 
    icon: Home, 
    name: 'Tout pour la maison', 
    items: ['Lessive', 'Vaisselle', 'Ménage'],
    color: '#4169E1',
    gradient: 'from-blue-900/50 to-blue-950/30',
    borderColor: 'border-blue-500/30',
    // 🔊 SPLASH - Eau qui coule + ding machine !
    sounds: [playHouseSound]
  },
  { 
    icon: Droplets, 
    name: 'Limonades & Boissons', 
    items: ['Sodas', 'Ice Tea', 'Jus', 'Eaux'],
    color: '#7CFC00',
    gradient: 'from-lime-900/50 to-lime-950/30',
    borderColor: 'border-lime-500/30',
    // 🔊 FIZZ - Bulles pétillantes !
    sounds: [playLemonadeSound]
  },
  { 
    icon: Dog, 
    name: 'Animaux', 
    items: ['Croquettes', 'Pâtées', 'Friandises'],
    color: '#8B4513',
    gradient: 'from-amber-900/50 to-amber-950/30',
    borderColor: 'border-amber-600/30',
    // 🔊 WOUF ou MIAOU aléatoire !
    sounds: [playDogSound, playCatSound]
  },
  { 
    icon: Heart, 
    name: 'Hygiène & Beauté', 
    items: ['Soins', 'Maquillage', 'Hygiène'],
    color: '#FF1493',
    gradient: 'from-fuchsia-900/50 to-fuchsia-950/30',
    borderColor: 'border-fuchsia-500/30',
    // 🔊 Sifflement d'admiration 😏 + Squish
    sounds: [playBeautySound, playHygieneSound]
  }
];

const ProductCard = ({ category, index }) => {
  const Icon = category.icon;
  
  // 🔊 Joue un son aléatoire parmi les sons de la catégorie
  const handleHover = () => {
    try {
      if (category.sounds && category.sounds.length > 0) {
        const randomSound = category.sounds[Math.floor(Math.random() * category.sounds.length)];
        randomSound();
      }
    } catch (e) {
      // Silent fail si le son ne fonctionne pas
      console.log('Sound error:', e);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
      className={`group relative rounded-2xl overflow-hidden bg-gradient-to-br ${category.gradient} border ${category.borderColor} backdrop-blur-sm cursor-pointer`}
      whileHover={{ scale: 1.03, y: -5 }}
      onHoverStart={handleHover}
    >
      {/* Hover glow */}
      <motion.div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${category.color}20 0%, transparent 70%)`
        }}
      />

      {/* Scan line */}
      <motion.div
        className="absolute inset-0 opacity-0 group-hover:opacity-100"
        initial={{ y: '-100%' }}
        whileHover={{ y: '100%' }}
        transition={{ duration: 1, ease: "linear" }}
        style={{
          background: `linear-gradient(transparent, ${category.color}30, transparent)`
        }}
      />

      <div className="relative p-5">
        {/* Icon */}
        <motion.div
          className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
          style={{
            background: `linear-gradient(135deg, ${category.color}30, ${category.color}10)`,
            border: `1px solid ${category.color}50`
          }}
          whileHover={{
            boxShadow: `0 0 20px ${category.color}60`,
            scale: 1.1
          }}
        >
          <Icon className="w-6 h-6" style={{ color: category.color }} />
        </motion.div>

        {/* Name */}
        <h3 className="text-white font-bold mb-2">{category.name}</h3>

        {/* Items */}
        <div className="flex flex-wrap gap-1">
          {category.items.map((item, i) => (
            <span 
              key={i}
              className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-gray-400"
            >
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* Corner accent */}
      <div 
        className="absolute top-0 right-0 w-16 h-16 opacity-20"
        style={{
          background: `linear-gradient(135deg, transparent 50%, ${category.color}40 50%)`
        }}
      />
    </motion.div>
  );
};

const ProductsShowcaseSection = () => {
  return (
    <section className="relative py-20 px-4">
      {/* Header */}
      <div className="text-center mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 mb-4"
        >
          <ShoppingBag className="w-4 h-4 text-purple-400" />
          <span className="text-purple-400 text-sm font-mono uppercase tracking-wider">Nos Rayons</span>
          <span className="text-xs text-gray-500">🔊 Survolez !</span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-4xl md:text-5xl font-black text-white mb-4"
        >
          +500 Produits <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Stellaires</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-gray-400 max-w-xl mx-auto"
        >
          Tout ce dont vous avez besoin, à portée de main. 7j/7, de midi à minuit.
        </motion.p>
      </div>

      {/* Products Grid - Bento Style */}
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {PRODUCT_CATEGORIES.map((category, index) => (
            <ProductCard key={category.name} category={category} index={index} />
          ))}
        </div>
      </div>

      {/* Disclaimers */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mt-8 text-center text-xs text-gray-600"
      >
        <p>* Puffs : Interdits aux mineurs, produits non thérapeutiques</p>
        <p>** CBD : Produits conformes à la réglementation, THC &lt; 0.3%</p>
      </motion.div>
    </section>
  );
};

export default ProductsShowcaseSection;
